import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  IconButton,
  Link,
  Popper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import { useState } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhoneIcon from '@mui/icons-material/Phone';
import CallIcon from '@mui/icons-material/Call';
import { API_POST, DBRequest, useConfig } from '../../../api/api';
import ExoDialog from '../../../components/exo/ExoDialog';
import { NewEmail } from '../../../components/new/NewEmail';
import { hasPermissions } from '../../../auth/hasPermissions';
import { useEmailClient } from '../../../components/email-system/useEmailClient';
import StarIcon from '@mui/icons-material/Star';

export const LeadNameAddress = ({
  name,
  subName,
  address,
  image,
  emails,
  telephoneNumbers,
  sourceId,
  sourceType,
  source,
  onNewInteraction,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const { emailCredentials } = useEmailClient();

  const replaceSpaceWithPlus = (str) => str.replace(/ /g, '+');

  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // call
  const [callAnchorEl, setCallAnchorEl] = useState(null);
  function handleOpenNewCall(e) {
    setCallAnchorEl(e.currentTarget);
  }

  // mail
  const [mailAnchorEl, setMailAnchorEl] = useState(null);
  function handleOpenNewMail(e) {
    setMailAnchorEl(e.currentTarget);
  }

  function requestDataViaForm(email = null) {
    DBRequest({
      config,
      path: `customer-data-form`,
      method: API_POST({
        recipientId: sourceId,
        recipientType: sourceType,
        emailAddress: email, //passing to which email it should be sent
      }),
      onResponse: onNewInteraction,
    });
  }

  // write email address to start data collection
  const [startData, setStartData] = useState(null);
  function handleEmailSelect(email) {
    setStartData({
      to: [{ type: sourceType, email, participant: source }],
    });
  }

  return (
    <Box className="flex flex-col w-full items-center gap-1">
      <Box className="flex gap-4 w-full">
        <ExoAvatar
          size="80px"
          picture={image?.temporaryUrl}
          name={name}
          type={sourceType === 'companyLead' ? 'company' : 'contact'}
        />
        <Box className="w-full flex flex-col items-start justify-start">
          <Box className="flex">
            <Typography variant="h3" textAlign="left">
              {name}
            </Typography>
            <Box className="w-[30px] min-w-[30px]" />
          </Box>
          {subName && (
            <Typography
              textAlign="left"
              fontWeight={500}
              className="opacity-60"
            >
              {subName}
            </Typography>
          )}
          <Box className="flex items-stretch gap-1 py-1">
            <ContactButton
              Icon={CallIcon}
              label={t('Call')}
              onClick={handleOpenNewCall}
            />
            <ContactButton
              Icon={EmailIcon}
              label={t('E-Mail')}
              onClick={handleOpenNewMail}
            />
          </Box>
          {address && (
            <Box className="pb-2">
              <Typography fontWeight={600} className="">
                {t('Address')}
              </Typography>
              <Box className="flex items-center gap-1">
                <Link
                  href={`https://www.google.de/maps/place/${address.street}+${address.houseNumber},+${address.zipCode}+${address.city}`}
                  target="_blank"
                >
                  <IconButton
                    className="w-full"
                    size="small"
                    disableElevation
                    sx={{
                      bgcolor: colors.info + '30',
                      border: `1px solid ${colors.info}80`,
                      ':hover': {
                        bgcolor: colors.info + '60',
                        border: `1px solid ${colors.info}80`,
                      },
                    }}
                  >
                    <GoogleIcon className="px-[1px]" />
                  </IconButton>
                </Link>
                <Link
                  href={`http://maps.apple.com/?address=${replaceSpaceWithPlus(
                    address.houseNumber
                  )},${replaceSpaceWithPlus(
                    address.street
                  )},${replaceSpaceWithPlus(address.city)},${address.zipCode}`}
                  target="_blank"
                >
                  <IconButton
                    className="w-full"
                    size="small"
                    disableElevation
                    sx={{
                      bgcolor: colors.info + '30',
                      border: `1px solid ${colors.info}80`,
                      ':hover': {
                        bgcolor: colors.info + '60',
                        border: `1px solid ${colors.info}80`,
                      },
                    }}
                  >
                    <AppleIcon className="pb-[2px] pr-[1px]" />
                  </IconButton>
                </Link>
                <Typography className="pb-1 pl-1 ">
                  {`${address.street} ${address.houseNumber}`}
                  <br />
                  {`${address.zipCode} ${address.city}`}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Popper open={Boolean(callAnchorEl)} anchorEl={callAnchorEl}>
        <ClickAwayListener onClickAway={() => setCallAnchorEl(null)}>
          <Box
            className="px-4 py-2 rounded-lg flex flex-col gap-1 backdrop-blur-md"
            sx={{
              background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
              border: `1px solid ${colors.selected}`,
              maxHeight: 500, // set max height as needed
              overflowY: 'auto',
            }}
          >
            <Typography fontWeight={600}>{t('Phonenumbers')}</Typography>
            {telephoneNumbers?.map((number) => (
              <Link href={`tel:${number.phoneNumber}`} key={number.id}>
                <Button
                  sx={{
                    border: `1px solid transparent`,
                    ':hover': {
                      bgcolor: colors.info + '30',
                      border: `1px solid ${colors.info}`,
                    },
                  }}
                >
                  <Typography className=" items-center flex">
                    <PhoneIcon className="mr-3" />
                    <b>{number.phoneType}</b>
                    <ChevronRightIcon className="mx-1" />
                    {number.phoneNumber}
                  </Typography>
                </Button>
              </Link>
            ))}
          </Box>
        </ClickAwayListener>
      </Popper>
      <Popper open={Boolean(mailAnchorEl)} anchorEl={mailAnchorEl}>
        <ClickAwayListener onClickAway={() => setMailAnchorEl(null)}>
          <Box
            className="px-4 py-2 rounded-lg flex flex-col gap-1 backdrop-blur-md"
            sx={{
              background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
              border: `1px solid ${colors.selected}`,
              maxHeight: 500, // set max height as needed
              overflowY: 'auto',
            }}
          >
            <Typography fontWeight={600}>{t('E-Mails')}</Typography>
            {emails?.map((element) => (
              <Link
                href={
                  hasPermissions('email-inbox') &&
                  emailCredentials?.data?.length > 0
                    ? undefined
                    : `mailto:${element.emailAddress}`
                }
                key={element.id}
              >
                <Button
                  sx={{
                    border: `1px solid transparent`,
                    ':hover': {
                      bgcolor: colors.info + '30',
                      border: `1px solid ${colors.info}`,
                    },
                  }}
                  onClick={
                    hasPermissions('email-inbox') &&
                    emailCredentials?.data?.length > 0
                      ? () => handleEmailSelect(element.emailAddress)
                      : undefined
                  }
                >
                  <Typography className=" items-center flex">
                    <EmailIcon className="mr-3" />
                    {element.isPrimary && (
                      <StarIcon
                        fontSize="small"
                        sx={{ color: colors.warning, mr: 1 }}
                      />
                    )}
                    <b>{element.emailType}</b>
                    <ChevronRightIcon className="mx-1" />
                    {element.emailAddress}
                  </Typography>
                </Button>
              </Link>
            ))}
            <Box className="pt-3 w-full flex flex-col">
              <Tooltip
                title={t(
                  'This e-mail contains a link to a form in which the lead can fill in their data and also correct/change it.'
                )}
              >
                <Button
                  onClick={() => setOpenRequestDialog(true)}
                  sx={{
                    border: `1px solid transparent`,
                    ':hover': {
                      bgcolor: colors.info + '30',
                      border: `1px solid ${colors.info}`,
                    },
                  }}
                >
                  <Typography className=" items-center flex">
                    <EmailIcon className="mr-3" />
                    {t('Request missing data by e-mail')}
                  </Typography>
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>

      <Dialog
        open={openRequestDialog}
        onClose={() => setOpenRequestDialog(false)}
        sx={{
          '.MuiPaper-root': {
            backgroundImage: 'unset!important',
            width: '600px',
          },
          '.MuiBackdrop-root': {
            backdropFilter: 'blur(1px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
      >
        <Box className="flex flex-col gap-4 p-4">
          <Typography variant="h4" fontWeight={500}>
            {t('Select E-Mail Address')}
          </Typography>

          {emails?.map((email) => (
            <Button
              key={email.id}
              variant={
                selectedEmail === email.emailAddress ? 'contained' : 'outlined'
              }
              onClick={() => setSelectedEmail(email.emailAddress)}
              sx={{
                justifyContent: 'flex-start',
                border:
                  selectedEmail === email.emailAddress
                    ? `1px solid ${colors.info}`
                    : `1px solid ${colors.card}50`,
              }}
            >
              <EmailIcon className="mr-2" />

              <Box className="flex flex-col items-start">
                <Box>
                  <Typography fontWeight={600}>
                    {email.isPrimary && (
                      <StarIcon
                        fontSize="small"
                        sx={{ color: colors.warning, mr: 1 }}
                      />
                    )}
                    {email.emailType}
                  </Typography>
                </Box>
                <Typography fontSize="14px" color="textSecondary">
                  {email.emailAddress}
                </Typography>
              </Box>
            </Button>
          ))}

          <Button
            variant="contained"
            color="success"
            disabled={!selectedEmail}
            onClick={() => {
              setOpenRequestDialog(false);
              requestDataViaForm(selectedEmail);
            }}
          >
            {t('Request missing data by e-mail')}
          </Button>
        </Box>
      </Dialog>

      <ExoDialog open={Boolean(startData)} onClose={() => setStartData(null)}>
        <NewEmail startData={startData} onNewMail={() => setStartData(null)} />
      </ExoDialog>
    </Box>
  );
};

const ContactButton = ({ onClick, Icon, label }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Button
      color="secondary"
      variant="contained"
      disableElevation
      onClick={onClick}
      sx={{
        bgcolor: colors.bgInput,
        ':hover': {
          bgcolor: colors.selected,
        },
        padding: '0px!important',
      }}
    >
      <Box className="h-full w-full flex items-center justify-center gap-1 px-2 py-1">
        <Icon
          sx={{
            fontSize: '20px',
          }}
        />
        <Typography fontWeight={500} fontSize="10px">
          {label}
        </Typography>
      </Box>
    </Button>
  );
};
